// Что человек уже видел. Живёт на устройстве и переживает перезагрузку.
//
// ## Сброс — только при смене ХОЗЯИНА, а не при выходе
//
// Выход из программы случается на любом конце сеанса, включая неявный от протухшего токена —
// то есть у того же самого человека, который сейчас войдёт обратно. Сброс «по выходу» означает,
// что подсказка приезжает снова и снова: в проекте-доноре владелец увидел так одно и то же
// пояснение десять раз подряд (13.08.2026). Поэтому память привязана к опознавателю человека:
// сменился он — начинаем с чистого листа, не сменился — молчим.
//
// ## Показ засчитывается фактом ОТРИСОВКИ, а не нажатием «Понятно»
//
// Уйти с экрана можно, не тронув подсказку: закрыть вкладку, нажать саму кнопку, к которой она
// указывает, уйти в другой раздел. Любой учёт «по закрытию» эти случаи теряет.

import type { Урок } from './уроки';

const КЛЮЧ = 'bark.hints';

/**
 * Показывать ли подсказки в автоматическом браузере.
 *
 * По умолчанию — нет, и это не про удобство проверок. Подсказка первого раза выезжает через
 * семьсот миллисекунд после того, как экран доехал, и накрывает собой ровно тот элемент, о
 * котором рассказывает. В прогоне из тысячи двухсот проверок она накрывала бы его тысячу
 * двести раз — и краснели бы не подсказки, а всё, что под ними. Автоматический браузер при
 * этом первым разом ни для кого не является: человека за ним нет.
 *
 * Посмотреть на них глазами всё равно надо, поэтому есть явное разрешение — оно ставится
 * снимающим скриптом и больше нигде.
 */
const РАЗРЕШЕНО_В_ПРОГОНЕ = 'bark.hints.show-in-automation';

export function подсказкиРазрешены(): boolean {
  const автомат = typeof navigator !== 'undefined' && navigator.webdriver;
  if (!автомат) {
    return true;
  }
  try {
    return localStorage.getItem(РАЗРЕШЕНО_В_ПРОГОНЕ) === 'да';
  } catch {
    return false;
  }
}

interface Память {
  /** Кому принадлежит память. Сменился — всё стирается. */
  хозяин: string;
  /** Что уже показано. */
  видел: string[];
}

function прочитать(): Память | null {
  try {
    const сырое = localStorage.getItem(КЛЮЧ);
    if (сырое === null) {
      return null;
    }
    const разобрано = JSON.parse(сырое) as Память;
    return typeof разобрано.хозяин === 'string' && Array.isArray(разобрано.видел)
      ? разобрано
      : null;
  } catch {
    // Хранилище недоступно или в нём мусор: считаем, что человек ничего не видел. Показать
    // подсказку лишний раз дешевле, чем спрятать её навсегда из-за одной битой строки.
    return null;
  }
}

function записать(память: Память): void {
  try {
    localStorage.setItem(КЛЮЧ, JSON.stringify(память));
  } catch {
    // Не записали — подсказка покажется ещё раз. Это неприятно, но не ломает работу.
  }
}

/** Видел ли этот человек этот урок. */
export function ужеВидел(хозяин: string, урок: Урок): boolean {
  const память = прочитать();
  return память !== null && память.хозяин === хозяин && память.видел.includes(урок);
}

/** Записать показ. Смена хозяина стирает чужое разом. */
export function запомнитьПоказ(хозяин: string, урок: Урок): void {
  const прежняя = прочитать();
  const своя = прежняя !== null && прежняя.хозяин === хозяин ? прежняя : { хозяин, видел: [] };
  if (своя.видел.includes(урок)) {
    return;
  }
  записать({ хозяин, видел: [...своя.видел, урок] });
}

/** Забыть всё показанное. Нужна только настройкам и проверкам. */
export function забытьПоказы(): void {
  try {
    localStorage.removeItem(КЛЮЧ);
  } catch {
    // Нечего забывать.
  }
}
